import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { endpoints } from '../api/endpoints';
import type { Role } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ConfirmDialog, Dialog } from '../components/a11y';
import { ErrorBox, formatDate, Loading, Notice, useAsync } from '../components/common';

const ROLES: Role[] = ['user', 'admin', 'super_admin'];
const MIN_PASSWORD = 8;

/** Роли, которые текущий администратор может назначить новому пользователю. */
export function assignableRoles(isSuperAdmin: boolean): Role[] {
  return isSuperAdmin ? ROLES : ['user'];
}

export function CreateUserDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (email: string) => void }) {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const roles = assignableRoles(hasRole('super_admin'));
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('user');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await endpoints.users.create({ email: email.trim(), full_name: fullName.trim(), password, role });
      onCreated(email.trim());
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={t('admin.createUser')} onClose={onClose}>
      <form className="form" onSubmit={(e) => void submit(e)}>
        <ErrorBox error={error} />
        <label>
          {t('auth.email')}
          <input type="email" required autoComplete="off" data-autofocus value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label>
          {t('auth.fullName')}
          <input required maxLength={255} value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </label>
        <label>
          {t('auth.password')}
          <input
            type="password"
            required
            minLength={MIN_PASSWORD}
            maxLength={128}
            autoComplete="new-password"
            aria-describedby="new-user-password-hint"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span id="new-user-password-hint" className="muted">
            {t('admin.passwordHint', { n: MIN_PASSWORD })}
          </span>
        </label>
        <label>
          {t('admin.role')}
          <select value={role} disabled={roles.length < 2} onChange={(e) => setRole(e.target.value as Role)}>
            {roles.map((r) => (
              <option key={r} value={r}>
                {t(`admin.roles.${r}`)}
              </option>
            ))}
          </select>
        </label>
        {roles.length < 2 && <p className="muted">{t('admin.roleRestricted')}</p>}
        <div className="actions">
          <button type="button" className="secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={busy || password.length < MIN_PASSWORD}>
            {t('common.create')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const { hasRole, user: me } = useAuth();
  const [search, setSearch] = useState('');
  const users = useAsync(() => endpoints.users.list({ q: search, limit: 200 }), [search]);
  const [error, setError] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<{ id: string; email: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canChangeRoles = hasRole('super_admin');

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await users.reload();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>{t('admin.users')}</h1>
        <button type="button" onClick={() => setCreating(true)}>
          {t('admin.createUser')}
        </button>
      </div>
      <div className="filters">
        <label>
          {t('common.search')}
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>
      {notice && <Notice>{notice}</Notice>}
      <ErrorBox error={users.error || error} onRetry={users.reload} />
      {creating && (
        <CreateUserDialog
          onClose={() => setCreating(false)}
          onCreated={(email) => {
            setNotice(t('admin.userCreated', { email }));
            void users.reload();
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={t('common.delete')}
          message={t('admin.deleteConfirm', { email: deleting.email })}
          onClose={() => setDeleting(null)}
          onConfirm={() => void act(() => endpoints.users.remove(deleting.id))}
        />
      )}
      {users.loading && <Loading />}
      <table>
        <thead>
          <tr>
            <th scope="col">{t('auth.email')}</th>
            <th scope="col">{t('auth.fullName')}</th>
            <th scope="col">{t('admin.role')}</th>
            <th scope="col">{t('admin.active')}</th>
            <th scope="col">{t('sessions.started')}</th>
            <th scope="col">{t('admin.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {(users.data ?? []).map((u) => (
            <tr key={u.id}>
              <td>{u.email}</td>
              <td>{u.full_name}</td>
              <td>
                {canChangeRoles && u.id !== me?.id ? (
                  <select
                    aria-label={`${t('admin.role')}: ${u.email}`}
                    value={u.role}
                    onChange={(e) => void act(() => endpoints.users.setRole(u.id, e.target.value as Role))}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {t(`admin.roles.${r}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  t(`admin.roles.${u.role}`)
                )}
              </td>
              <td>
                <input
                  type="checkbox"
                  aria-label={`${t('admin.active')}: ${u.email}`}
                  checked={u.is_active}
                  disabled={u.id === me?.id}
                  onChange={(e) => void act(() => endpoints.users.setActive(u.id, e.target.checked))}
                />
              </td>
              <td>{formatDate(u.created_at, i18n.language)}</td>
              <td>
                {u.id !== me?.id && (
                  <button
                    type="button"
                    className="danger"
                    aria-label={`${t('common.delete')}: ${u.email}`}
                    onClick={() => setDeleting({ id: u.id, email: u.email })}
                  >
                    {t('common.delete')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AuditPage() {
  const { t, i18n } = useTranslation();
  const { hasRole } = useAuth();
  const [action, setAction] = useState('');
  const events = useAsync(() => endpoints.audit.events({ action, limit: 200 }), [action]);
  const [verify, setVerify] = useState<{ valid: boolean } | null>(null);
  const [error, setError] = useState<unknown>(null);

  return (
    <div>
      <h1>{t('admin.audit')}</h1>
      <div className="filters">
        <label>
          {t('admin.filterAction')}
          <input value={action} onChange={(e) => setAction(e.target.value)} placeholder="user.login" />
        </label>
        {hasRole('super_admin') && (
          <button
            type="button"
            onClick={async () => {
              setError(null);
              try {
                setVerify(await endpoints.audit.verify());
              } catch (err) {
                setError(err);
              }
            }}
          >
            {t('admin.verifyChain')}
          </button>
        )}
      </div>
      {verify &&
        (verify.valid ? (
          <Notice>{t('admin.chainValid')}</Notice>
        ) : (
          <p role="alert" className="alert alert-error">
            {t('admin.chainBroken')}
          </p>
        ))}
      <ErrorBox error={events.error || error} onRetry={events.reload} />
      {events.loading && <Loading />}
      <table>
        <thead>
          <tr>
            <th scope="col">{t('admin.time')}</th>
            <th scope="col">{t('admin.service')}</th>
            <th scope="col">{t('admin.action')}</th>
            <th scope="col">{t('admin.actor')}</th>
            <th scope="col">{t('admin.target')}</th>
            <th scope="col">{t('admin.outcome')}</th>
          </tr>
        </thead>
        <tbody>
          {(events.data ?? []).map((e) => (
            <tr key={e.event_id}>
              <td>{formatDate(e.occurred_at, i18n.language)}</td>
              <td>{e.service}</td>
              <td>{e.action}</td>
              <td>{e.actor_id ?? '—'}</td>
              <td>{e.target_type ? `${e.target_type}:${e.target_id ?? ''}` : '—'}</td>
              <td>{e.outcome}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
